import type { EventTypeMap } from '@models/events.model';

/**
 * Class decorator that tags an event token class with its JSON discriminator name.
 * Sets `static $type` on the class at runtime — the value mirrors the one declared
 * as a `const` literal on the class itself so TypeScript can narrow the union.
 *
 * Usage:
 *   @className('EmployeeUpdatedEvent')
 *   export class EmployeeUpdatedEventToken {
 *     static readonly $type = 'EmployeeUpdatedEvent' as const;
 *   }
 *
 *   // Consumer:
 *   eventBus.on(EmployeeUpdatedEventToken).subscribe(e => { ... })
 */
export function className(name: string): ClassDecorator {
  return (ctor) => {
    (ctor as Record<string, unknown>)['$type'] = name;
  };
}

/**
 * Shape of a class token accepted by EventBusService.on().
 * K is constrained to the keys of EventTypeMap (i.e. known C# class names).
 * TypeScript resolves EventTypeMap[K] to the correct concrete event interface.
 */
export type EventToken<K extends keyof EventTypeMap> = { readonly $type: K };
